import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from 'recharts';
import { authService, reportesService } from '../services/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import MapaUbicacionHeatmap from '../components/MapaUbicacionHeatmap';
import ConfirmPopup from '../components/ConfirmPopup';
import './Estadisticas.css';

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b', '#fa709a', '#fee140', '#30cfd0'];

/** Colores por medio para "De dónde nos conocieron" (referencia a cada red/medio). */
const getFuenteColor = (nombre, fallbackIndex = 0) => {
  if (!nombre) return COLORS[fallbackIndex % COLORS.length];
  const n = String(nombre).toLowerCase();
  if (n.includes('whatsapp') || n.includes('wpp') || n.includes('whats')) return '#25D366'; // verde WhatsApp
  if (n.includes('instagram') || n.includes('ig')) return '#833AB4'; // violeta Instagram
  if (n.includes('facebook') || n.includes('fb')) return '#1877F2'; // azul Facebook
  if (n.includes('twitter') || n.includes('x')) return '#000000'; // negro X
  if (n.includes('google') || n.includes('búsqueda') || n.includes('busqueda')) return '#4285F4'; // azul Google
  if (n.includes('linkedin')) return '#0A66C2'; // azul LinkedIn
  if (n.includes('youtube')) return '#FF0000'; // rojo YouTube
  if (n.includes('tiktok')) return '#000000'; // negro TikTok
  if (n.includes('referido') || n.includes('amigo') || n.includes('conocido')) return '#6B7280'; // gris
  return COLORS[fallbackIndex % COLORS.length];
};

const Estadisticas = () => {
  const navigate = useNavigate();
  const reportRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [compareYear, setCompareYear] = useState('');
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];
  const [exportingPdf, setExportingPdf] = useState(false);
  const [mostrarPorCurso, setMostrarPorCurso] = useState(false);
  const [cursoSeleccionado, setCursoSeleccionado] = useState('');
  const [generoPorCurso, setGeneroPorCurso] = useState(false);
  const [generoCursoId, setGeneroCursoId] = useState('');
  const [fuentePorCurso, setFuentePorCurso] = useState(false);
  const [fuenteCursoId, setFuenteCursoId] = useState('');
  const [situacionPorCurso, setSituacionPorCurso] = useState(false);
  const [situacionCursoId, setSituacionCursoId] = useState('');
  const [estudioPorCurso, setEstudioPorCurso] = useState(false);
  const [estudioCursoId, setEstudioCursoId] = useState('');
  const [ubicacionPorCurso, setUbicacionPorCurso] = useState(false);
  const [ubicacionCursoId, setUbicacionCursoId] = useState('');
  const [ubicacionTablaExpandida, setUbicacionTablaExpandida] = useState(false);
  const [asistenciaPorCurso, setAsistenciaPorCurso] = useState(false);
  const [asistenciaCursoId, setAsistenciaCursoId] = useState('');
  const [rangoEtarioPorCurso, setRangoEtarioPorCurso] = useState(false);
  const [rangoEtarioCursoId, setRangoEtarioCursoId] = useState('');

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    loadEstadisticas();
  }, [navigate, year, compareYear, generoPorCurso, generoCursoId, fuentePorCurso, fuenteCursoId, situacionPorCurso, situacionCursoId, estudioPorCurso, estudioCursoId, ubicacionPorCurso, ubicacionCursoId, asistenciaPorCurso, asistenciaCursoId, rangoEtarioPorCurso, rangoEtarioCursoId]);

  const loadEstadisticas = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (year && year !== '') params.year = Number(year);
      if (compareYear && parseInt(compareYear, 10) !== Number(year)) params.compare_year = parseInt(compareYear, 10);
      if (generoPorCurso && generoCursoId) params.curso_id = Number(generoCursoId);
      if (fuentePorCurso && fuenteCursoId) params.fuente_curso_id = Number(fuenteCursoId);
      if (situacionPorCurso && situacionCursoId) params.situacion_curso_id = Number(situacionCursoId);
      if (estudioPorCurso && estudioCursoId) params.estudio_curso_id = Number(estudioCursoId);
      if (ubicacionPorCurso && ubicacionCursoId) params.ubicacion_curso_id = Number(ubicacionCursoId);
      if (asistenciaPorCurso && asistenciaCursoId) params.asistencia_curso_id = Number(asistenciaCursoId);
      if (rangoEtarioPorCurso && rangoEtarioCursoId) params.rango_etario_curso_id = Number(rangoEtarioCursoId);
      const result = await reportesService.getDashboard(params);
      setData(result);
      setUbicacionTablaExpandida(false);
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al cargar estadísticas';
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    if (!reportRef.current) return;
    setExportingPdf(true);
    try {
      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#f5f7fa',
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      if (pdfHeight > pdf.internal.pageSize.getHeight()) {
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -(pdf.internal.pageSize.getHeight()), pdfWidth, pdfHeight);
      }
      pdf.save(`estadisticas-campus-${year || 'todos'}${compareYear ? `-vs-${compareYear}` : ''}.pdf`);
    } catch (e) {
      console.error(e);
      setError('Error al generar el PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="estadisticas-container">
        <h1>Estadísticas</h1>
        <div className="estadisticas-loading">Cargando estadísticas...</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="estadisticas-container">
        <h1>Estadísticas</h1>
        <ConfirmPopup
          open
          title="Error"
          message={error}
          confirmLabel="Aceptar"
          cancelLabel={null}
          onConfirm={() => navigate('/dashboard')}
          onCancel={() => navigate('/dashboard')}
          variant="danger"
        />
      </div>
    );
  }

  const ru = data?.resumen_usuarios || {};
  const ins = data?.inscripciones || {};
  const genero = data?.genero || [];
  const fuente = data?.fuente_informacion || [];
  const situacionLaboral = data?.situacion_laboral || [];
  const nivelEstudio = data?.nivel_estudio || [];
  const inscripcionesPorUbicacion = data?.inscripciones_por_ubicacion || [];
  const ubicacionFilasVisibles = ubicacionTablaExpandida
    ? inscripcionesPorUbicacion
    : inscripcionesPorUbicacion.slice(0, 5);
  const asistencias = data?.asistencias || {};
  const asistenciasPorCurso = asistencias.por_curso || [];
  const asistenciasCursoFiltrado = asistencias.curso_filtrado || null;
  const formatFechaAsistencia = (raw) => {
    if (!raw) return '—';
    const s = String(raw).slice(0, 10);
    const [y, m, d] = s.split('-').map(Number);
    if (!y || !m || !d) return s;
    return new Date(y, m - 1, d).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };
  const chartAsistenciasPorCurso = asistenciasPorCurso.map((c) => ({
    nombre: c.nombre,
    cantidad: c.cantidad ?? 0,
  }));
  const chartAsistenciasPorFecha = (asistenciasCursoFiltrado?.por_fecha || []).map((r) => ({
    fecha: formatFechaAsistencia(r.fecha),
    fechaRaw: r.fecha,
    cantidad: r.cantidad ?? 0,
  }));
  const rangoEtario = data?.rango_etario || [];
  const porCurso = ins.por_curso || [];
  const porAnio = ins.por_anio || {};
  const cursosDelAnio = ins.cursos_del_anio || [];
  const cursosFiltroAsistencia = cursosDelAnio.length > 0
    ? cursosDelAnio
    : asistenciasPorCurso.map((c) => ({ id: c.curso_id, nombre: c.nombre }));

  const totalInscripcionesChart = (year !== '' && year && porAnio[String(year)] != null)
    ? porAnio[String(year)]
    : (ins.total ?? 0);
  const totalIniciadosChart = ins.iniciados ?? 0;

  const chartInscripcionesVsIniciados = mostrarPorCurso && cursoSeleccionado
    ? (() => {
        const curso = porCurso.find((c) => String(c.curso_id) === String(cursoSeleccionado));
        if (!curso) return [];
        return [
          { nombre: 'Inscriptos', cantidad: curso.inscripciones ?? 0 },
          { nombre: 'Iniciados', cantidad: curso.iniciados ?? 0 },
        ];
      })()
    : [
        { nombre: 'Inscripciones', cantidad: totalInscripcionesChart },
        { nombre: 'Iniciados', cantidad: totalIniciadosChart },
      ];

  const legendInscripcionesVsIniciados = (
    <div className="recharts-legend-wrapper" style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginTop: '0.5rem' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        <span style={{ display: 'inline-block', width: 14, height: 14, backgroundColor: COLORS[0], borderRadius: 2 }} />
        {mostrarPorCurso && cursoSeleccionado ? 'Inscriptos' : 'Inscripciones'}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        <span style={{ display: 'inline-block', width: 14, height: 14, backgroundColor: COLORS[1], borderRadius: 2 }} />
        Iniciados
      </span>
    </div>
  );

  return (
    <div className="estadisticas-container">
      <div className="estadisticas-header">
        <h1>Estadísticas</h1>
        <div className="estadisticas-actions">
          <div className="estadisticas-filtros">
            <label>
              Año
              <select value={year} onChange={(e) => setYear(e.target.value === '' ? '' : Number(e.target.value))}>
                <option value="">Todos los años</option>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </label>
            <label>
              Comparar con año
              <select value={compareYear} onChange={(e) => setCompareYear(e.target.value)}>
                <option value="">— No comparar —</option>
                {yearOptions
                  .filter((y) => String(y) !== String(year))
                  .map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
              </select>
            </label>
          </div>
          <Link to="/dashboard/estadisticas/informe" className="estadisticas-btn-export" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
            📋 Crear informe
          </Link>
          <button
            type="button"
            className="estadisticas-btn-export"
            onClick={handleExportPdf}
            disabled={exportingPdf}
          >
            {exportingPdf ? 'Generando…' : '📄 Exportar a PDF'}
          </button>
        </div>
      </div>

      <div className="estadisticas-content" ref={reportRef}>
        {/* Resumen de usuarios */}
        <section className="estadisticas-section estadisticas-resumen">
          <h2>Resumen de usuarios</h2>
          <div className="estadisticas-cards">
            <div className="estadisticas-card">
              <span className="estadisticas-card-value">{ru.total ?? 0}</span>
              <span className="estadisticas-card-label">Total usuarios registrados</span>
            </div>
            <div className="estadisticas-card">
              <span className="estadisticas-card-value">{ru.estudiantes ?? 0}</span>
              <span className="estadisticas-card-label">Estudiantes</span>
            </div>
            <div className="estadisticas-card">
              <span className="estadisticas-card-value">{ru.equipo ?? 0}</span>
              <span className="estadisticas-card-label">Equipo (Profesor, Tutor, Pasante, Coordinador)</span>
            </div>
            <div className="estadisticas-card">
              <span className="estadisticas-card-value">{ru.ayudantes ?? 0}</span>
              <span className="estadisticas-card-label">Ayudantes</span>
            </div>
          </div>
        </section>

        {/* Inscripciones */}
        <section className="estadisticas-section">
          <h2>Inscripciones {year !== '' && year ? `(${year}${data?.filtros?.compare_year ? ` vs ${data.filtros.compare_year}` : ''})` : '(todos los años)'}</h2>
          <div className="estadisticas-inscripciones-totales">
            <p><strong>Total de inscripciones:</strong> {ins.total ?? 0}</p>
            <p><strong>Iniciados (presente en primera clase):</strong> {totalIniciadosChart}</p>
            {Object.keys(porAnio).length > 0 && (
              <p className="estadisticas-por-anio">
                Por año: {Object.entries(porAnio).map(([y, n]) => `${y}: ${n}`).join(' | ')}
              </p>
            )}
          </div>
          <div className="estadisticas-chart-wrap">
            <h3>Inscripciones vs Iniciados</h3>
            <div className="estadisticas-chart-opciones">
              <label className="estadisticas-checkbox-label">
                <input
                  type="checkbox"
                  checked={mostrarPorCurso}
                  onChange={(e) => {
                    setMostrarPorCurso(e.target.checked);
                    if (!e.target.checked) setCursoSeleccionado('');
                  }}
                />
                <span>Mostrar por curso</span>
              </label>
              {mostrarPorCurso && cursosDelAnio.length > 0 && (
                <label className="estadisticas-select-curso-label">
                  Curso
                  <select
                    value={cursoSeleccionado}
                    onChange={(e) => setCursoSeleccionado(e.target.value)}
                    className="estadisticas-select-curso"
                  >
                    <option value="">Seleccionar curso...</option>
                    {cursosDelAnio.map((c) => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            {chartInscripcionesVsIniciados.length > 0 && (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartInscripcionesVsIniciados} margin={{ top: 20, right: 30, left: 20, bottom: 60 }} barCategoryGap={4}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nombre" angle={0} textAnchor="middle" height={40} padding={{ left: 80, right: 80 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend content={legendInscripcionesVsIniciados} />
                  <Bar dataKey="cantidad" name="Cantidad" barSize={150} isAnimationActive={false}>
                    {chartInscripcionesVsIniciados.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.nombre === 'Iniciados' ? COLORS[1] : COLORS[0]} />
                    ))}
                    <LabelList dataKey="cantidad" position="center" fill="#fff" style={{ fontSize: 12, fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
            {mostrarPorCurso && cursoSeleccionado && chartInscripcionesVsIniciados.length === 0 && (
              <p className="estadisticas-sin-datos">Seleccioná un curso para ver inscriptos e iniciados.</p>
            )}
            {!mostrarPorCurso && chartInscripcionesVsIniciados.length === 0 && (
              <p className="estadisticas-sin-datos">No hay datos de inscripciones para el período seleccionado.</p>
            )}
          </div>
        </section>

        {/* Mapa de calor: De dónde se inscribieron */}
        <section className="estadisticas-section">
          <h2>De dónde se inscribieron</h2>
          <div className="estadisticas-chart-opciones" style={{ marginBottom: '0.75rem' }}>
            <label className="estadisticas-checkbox-label">
              <input
                type="checkbox"
                checked={ubicacionPorCurso}
                onChange={(e) => {
                  setUbicacionPorCurso(e.target.checked);
                  if (!e.target.checked) setUbicacionCursoId('');
                }}
              />
              <span>Mostrar por curso</span>
            </label>
            {ubicacionPorCurso && cursosDelAnio.length > 0 && (
              <label className="estadisticas-select-curso-label">
                Curso
                <select
                  value={ubicacionCursoId}
                  onChange={(e) => setUbicacionCursoId(e.target.value)}
                  className="estadisticas-select-curso"
                >
                  <option value="">Seleccionar curso...</option>
                  {cursosDelAnio.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {!ubicacionPorCurso && data?.inscripciones?.total_personas_anio != null && (
            <p className="estadisticas-ayuda" style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: '#555' }}>
              Total del año: <strong>{data.inscripciones.total_personas_anio} personas</strong> (por ubicación completa).
            </p>
          )}
          {ubicacionPorCurso && (
            <p className="estadisticas-ayuda" style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: '#555' }}>
              Inscriptos solo en el curso elegido.
            </p>
          )}
          {inscripcionesPorUbicacion.length > 0 ? (
            <>
              <div className="estadisticas-chart-wrap estadisticas-mapa-chart">
                <MapaUbicacionHeatmap
                  heatPoints={(data?.mapa_puntos || []).map((p) => [p.lat, p.lng, Math.max(1, Math.min(p.cantidad, 100))])}
                  mapaPuntos={data?.mapa_puntos || []}
                  height={380}
                />
              </div>
              <div className="estadisticas-chart-wrap estadisticas-heatmap-wrap">
                <table className="estadisticas-heatmap-table">
                  <thead>
                    <tr>
                      <th>País</th>
                      <th>Provincia</th>
                      <th>Partido</th>
                      <th>Barrio</th>
                      <th className="estadisticas-heatmap-th-cant">Cantidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ubicacionFilasVisibles.map((row, i) => {
                      const maxVal = Math.max(...inscripcionesPorUbicacion.map((d) => d.cantidad), 1);
                      const ratio = row.cantidad / maxVal;
                      const lightness = 92 - 52 * ratio;
                      const bg = `hsl(38, 95%, ${lightness}%)`;
                      return (
                        <tr key={i}>
                          <td>{row.pais}</td>
                          <td>{row.provincia}</td>
                          <td>{row.partido}</td>
                          <td>{row.barrio}</td>
                          <td className="estadisticas-heatmap-cell" style={{ backgroundColor: bg, color: ratio > 0.5 ? '#fff' : '#333' }}>{row.cantidad}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {inscripcionesPorUbicacion.length > 5 && (
                  <div className="estadisticas-tabla-toggle">
                    {!ubicacionTablaExpandida ? (
                      <button
                        type="button"
                        className="estadisticas-btn-link"
                        onClick={() => setUbicacionTablaExpandida(true)}
                      >
                        Ver más ({inscripcionesPorUbicacion.length - 5} restantes)
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="estadisticas-btn-link"
                        onClick={() => setUbicacionTablaExpandida(false)}
                      >
                        Ocultar
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="estadisticas-sin-datos">
              {ubicacionPorCurso && !ubicacionCursoId ? 'Seleccioná un curso para ver la ubicación.' : 'No hay datos de ubicación para el período seleccionado.'}
            </p>
          )}
        </section>

        {/* Asistencias */}
        <section className="estadisticas-section">
          <h2>Asistencias {year !== '' && year ? `(${year})` : '(todos los años)'}</h2>
          <div className="estadisticas-chart-opciones" style={{ marginBottom: '0.75rem' }}>
            <label className="estadisticas-checkbox-label">
              <input
                type="checkbox"
                checked={asistenciaPorCurso}
                onChange={(e) => {
                  setAsistenciaPorCurso(e.target.checked);
                  if (!e.target.checked) setAsistenciaCursoId('');
                }}
              />
              <span>Mostrar por curso</span>
            </label>
            {asistenciaPorCurso && cursosFiltroAsistencia.length > 0 && (
              <label className="estadisticas-select-curso-label">
                Curso
                <select
                  value={asistenciaCursoId}
                  onChange={(e) => setAsistenciaCursoId(e.target.value)}
                  className="estadisticas-select-curso"
                >
                  <option value="">Seleccionar curso...</option>
                  {cursosFiltroAsistencia.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <div className="estadisticas-cards" style={{ marginBottom: '1rem' }}>
            <div className="estadisticas-card">
              <span className="estadisticas-card-value">{asistencias.total ?? 0}</span>
              <span className="estadisticas-card-label">Asistencias totales (todos los cursos)</span>
            </div>
            {asistenciaPorCurso && asistenciaCursoId && asistenciasCursoFiltrado && (
              <div className="estadisticas-card">
                <span className="estadisticas-card-value">{asistenciasCursoFiltrado.cantidad ?? 0}</span>
                <span className="estadisticas-card-label">Total en {asistenciasCursoFiltrado.nombre}</span>
              </div>
            )}
          </div>
          {asistenciaPorCurso && !asistenciaCursoId ? (
            <p className="estadisticas-sin-datos">Seleccioná un curso para ver sus asistencias por clase.</p>
          ) : asistenciaPorCurso && asistenciaCursoId ? (
            chartAsistenciasPorFecha.length > 0 ? (
              <div className="estadisticas-chart-wrap">
                <h3>Asistencias por clase — {asistenciasCursoFiltrado?.nombre || 'Curso'}</h3>
                <p className="estadisticas-ayuda" style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: '#555' }}>
                  Presentes por fecha de clase. Total del curso: <strong>{asistenciasCursoFiltrado?.cantidad ?? 0}</strong>.
                </p>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={chartAsistenciasPorFecha}
                    margin={{ top: 20, right: 20, left: 10, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="fecha"
                      angle={-35}
                      textAnchor="end"
                      height={70}
                      interval={0}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip
                      formatter={(value) => [value, 'Presentes']}
                      labelFormatter={(label) => `Clase: ${label}`}
                    />
                    <Bar dataKey="cantidad" name="Presentes" fill="#43e97b" barSize={36}>
                      <LabelList dataKey="cantidad" position="top" style={{ fontSize: 12, fontWeight: 600 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="estadisticas-sin-datos">No hay asistencias registradas para este curso en el período seleccionado.</p>
            )
          ) : chartAsistenciasPorCurso.length > 0 ? (
            <div className="estadisticas-chart-wrap">
              <h3>Asistencias por curso</h3>
              <ResponsiveContainer width="100%" height={Math.min(420, 80 + chartAsistenciasPorCurso.length * 36)}>
                <BarChart
                  data={chartAsistenciasPorCurso}
                  layout="vertical"
                  margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="nombre" width={140} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="cantidad" name="Presentes" fill="#43e97b" barSize={22}>
                    <LabelList dataKey="cantidad" position="right" style={{ fontSize: 12, fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="estadisticas-sin-datos">No hay asistencias registradas para el período seleccionado.</p>
          )}
        </section>

        {/* Rango etario */}
        <section className="estadisticas-section">
          <h2>Rango etario</h2>
          <div className="estadisticas-chart-opciones" style={{ marginBottom: '0.75rem' }}>
            <label className="estadisticas-checkbox-label">
              <input
                type="checkbox"
                checked={rangoEtarioPorCurso}
                onChange={(e) => {
                  setRangoEtarioPorCurso(e.target.checked);
                  if (!e.target.checked) setRangoEtarioCursoId('');
                }}
              />
              <span>Mostrar por curso</span>
            </label>
            {rangoEtarioPorCurso && cursosDelAnio.length > 0 && (
              <label className="estadisticas-select-curso-label">
                Curso
                <select
                  value={rangoEtarioCursoId}
                  onChange={(e) => setRangoEtarioCursoId(e.target.value)}
                  className="estadisticas-select-curso"
                >
                  <option value="">Seleccionar curso...</option>
                  {cursosDelAnio.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {!rangoEtarioPorCurso && data?.inscripciones?.total_personas_anio != null && (
            <p className="estadisticas-ayuda" style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: '#555' }}>
              Total del año: <strong>{data.inscripciones.total_personas_anio} personas</strong>. Edad al cierre del año.
            </p>
          )}
          {rangoEtarioPorCurso && (
            <p className="estadisticas-ayuda" style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: '#555' }}>
              Inscriptos solo en el curso elegido. Edad al cierre del año.
            </p>
          )}
          {rangoEtario.length > 0 ? (
            <div className="estadisticas-chart-wrap">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={rangoEtario} layout="vertical" margin={{ top: 10, right: 30, left: 80, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="nombre" width={72} />
                  <Tooltip />
                  <Bar dataKey="cantidad" name="Personas" fill="#4facfe">
                    <LabelList dataKey="cantidad" position="center" fill="#fff" style={{ fontSize: 12, fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="estadisticas-sin-datos">
              {rangoEtarioPorCurso && !rangoEtarioCursoId ? 'Seleccioná un curso para ver el rango etario.' : 'No hay datos de rango etario para el período seleccionado.'}
            </p>
          )}
        </section>

        {/* Género */}
        <section className="estadisticas-section">
          <h2>Género de inscriptos</h2>
          <div className="estadisticas-chart-opciones" style={{ marginBottom: '0.75rem' }}>
            <label className="estadisticas-checkbox-label">
              <input
                type="checkbox"
                checked={generoPorCurso}
                onChange={(e) => {
                  setGeneroPorCurso(e.target.checked);
                  if (!e.target.checked) setGeneroCursoId('');
                }}
              />
              <span>Mostrar por curso</span>
            </label>
            {generoPorCurso && cursosDelAnio.length > 0 && (
              <label className="estadisticas-select-curso-label">
                Curso
                <select
                  value={generoCursoId}
                  onChange={(e) => setGeneroCursoId(e.target.value)}
                  className="estadisticas-select-curso"
                >
                  <option value="">Seleccionar curso...</option>
                  {cursosDelAnio.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {!generoPorCurso && data?.inscripciones?.total_personas_anio != null && (
            <p className="estadisticas-ayuda" style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: '#555' }}>
              Total del año: <strong>{data.inscripciones.total_personas_anio} personas</strong> (cada persona cuenta una vez aunque se anote en más de un curso).
            </p>
          )}
          {generoPorCurso && (
            <p className="estadisticas-ayuda" style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: '#555' }}>
              Inscriptos solo en el curso elegido. El total del año ({data?.inscripciones?.total_personas_anio ?? '—'} personas) cuenta a cada persona una sola vez.
            </p>
          )}
          {genero.length > 0 ? (
            <div className="estadisticas-chart-wrap estadisticas-pie-wrap">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={genero}
                    dataKey="cantidad"
                    nameKey="nombre"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ nombre, cantidad, midAngle, outerRadius, cx, cy }) => {
                      const RADIAN = Math.PI / 180;
                      const rOuter = outerRadius + 24;
                      const xOut = cx + rOuter * Math.cos(-midAngle * RADIAN);
                      const yOut = cy + rOuter * Math.sin(-midAngle * RADIAN);
                      const isRight = Math.cos(-midAngle * RADIAN) >= 0;
                      const rIn = outerRadius * 0.6;
                      const xIn = cx + rIn * Math.cos(-midAngle * RADIAN);
                      const yIn = cy + rIn * Math.sin(-midAngle * RADIAN);
                      return (
                        <g>
                          <line x1={cx + outerRadius * Math.cos(-midAngle * RADIAN)} y1={cy + outerRadius * Math.sin(-midAngle * RADIAN)} x2={xOut} y2={yOut} stroke="#666" strokeWidth={1} />
                          <text x={xOut} y={yOut} fill="#333" textAnchor={isRight ? 'start' : 'end'} dominantBaseline="central" style={{ fontSize: 12, fontWeight: 600 }} dx={isRight ? 6 : -6}>
                            {nombre}
                          </text>
                          <text x={xIn} y={yIn} fill="#fff" textAnchor="middle" dominantBaseline="central" style={{ fontSize: 13, fontWeight: 600 }}>
                            {cantidad}
                          </text>
                        </g>
                      );
                    }}
                    labelLine={false}
                  >
                    {genero.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="estadisticas-sin-datos">
              {generoPorCurso && !generoCursoId ? 'Seleccioná un curso para ver el género de inscriptos.' : 'No hay datos de género para el período seleccionado.'}
            </p>
          )}
        </section>

        {/* Fuente de información */}
        <section className="estadisticas-section">
          <h2>¿De dónde nos conocieron?</h2>
          <div className="estadisticas-chart-opciones" style={{ marginBottom: '0.75rem' }}>
            <label className="estadisticas-checkbox-label">
              <input
                type="checkbox"
                checked={fuentePorCurso}
                onChange={(e) => {
                  setFuentePorCurso(e.target.checked);
                  if (!e.target.checked) setFuenteCursoId('');
                }}
              />
              <span>Mostrar por curso</span>
            </label>
            {fuentePorCurso && cursosDelAnio.length > 0 && (
              <label className="estadisticas-select-curso-label">
                Curso
                <select
                  value={fuenteCursoId}
                  onChange={(e) => setFuenteCursoId(e.target.value)}
                  className="estadisticas-select-curso"
                >
                  <option value="">Seleccionar curso...</option>
                  {cursosDelAnio.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {!fuentePorCurso && data?.inscripciones?.total_personas_anio != null && (
            <p className="estadisticas-ayuda" style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: '#555' }}>
              Total del año: <strong>{data.inscripciones.total_personas_anio} personas</strong> (cada persona cuenta una vez aunque se anote en más de un curso).
            </p>
          )}
          {fuentePorCurso && (
            <p className="estadisticas-ayuda" style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: '#555' }}>
              Inscriptos solo en el curso elegido. El total del año ({data?.inscripciones?.total_personas_anio ?? '—'} personas) cuenta a cada persona una sola vez.
            </p>
          )}
          {fuente.length > 0 ? (() => {
            const totalFuente = fuente.reduce((s, d) => s + (d.cantidad || 0), 0);
            return (
            <div className="estadisticas-chart-wrap estadisticas-pie-wrap">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={fuente}
                    dataKey="cantidad"
                    nameKey="nombre"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={58}
                    label={({ nombre, cantidad, midAngle, outerRadius, cx, cy }) => {
                      const RADIAN = Math.PI / 180;
                      const rOuter = outerRadius + 28;
                      const xOut = cx + rOuter * Math.cos(-midAngle * RADIAN);
                      const yOut = cy + rOuter * Math.sin(-midAngle * RADIAN);
                      const isRight = Math.cos(-midAngle * RADIAN) >= 0;
                      const pct = totalFuente ? ((cantidad / totalFuente) * 100).toFixed(1) : '0';
                      return (
                        <g>
                          <line x1={cx + outerRadius * Math.cos(-midAngle * RADIAN)} y1={cy + outerRadius * Math.sin(-midAngle * RADIAN)} x2={xOut} y2={yOut} stroke="#999" strokeWidth={1} />
                          <text x={xOut} y={yOut} fill="#333" textAnchor={isRight ? 'start' : 'end'} dominantBaseline="central" style={{ fontSize: 12, fontWeight: 600 }} dx={isRight ? 6 : -6}>
                            {nombre}
                          </text>
                          <text x={xOut} y={yOut} fill="#555" textAnchor={isRight ? 'start' : 'end'} dominantBaseline="central" style={{ fontSize: 12 }} dx={isRight ? 6 : -6} dy={14}>
                            {pct}%
                          </text>
                        </g>
                      );
                    }}
                    labelLine={false}
                  >
                    {fuente.map((entry, i) => (
                      <Cell key={i} fill={getFuenteColor(entry.nombre, i)} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} (${totalFuente ? ((value / totalFuente) * 100).toFixed(1) : 0}%)`, 'Personas']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            );
          })() : (
            <p className="estadisticas-sin-datos">
              {fuentePorCurso && !fuenteCursoId ? 'Seleccioná un curso para ver la fuente de información.' : 'No hay datos de fuente de información para el período seleccionado.'}
            </p>
          )}
        </section>

        {/* Situación laboral */}
        <section className="estadisticas-section">
          <h2>Situación laboral</h2>
          <div className="estadisticas-chart-opciones" style={{ marginBottom: '0.75rem' }}>
            <label className="estadisticas-checkbox-label">
              <input
                type="checkbox"
                checked={situacionPorCurso}
                onChange={(e) => {
                  setSituacionPorCurso(e.target.checked);
                  if (!e.target.checked) setSituacionCursoId('');
                }}
              />
              <span>Mostrar por curso</span>
            </label>
            {situacionPorCurso && cursosDelAnio.length > 0 && (
              <label className="estadisticas-select-curso-label">
                Curso
                <select
                  value={situacionCursoId}
                  onChange={(e) => setSituacionCursoId(e.target.value)}
                  className="estadisticas-select-curso"
                >
                  <option value="">Seleccionar curso...</option>
                  {cursosDelAnio.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {!situacionPorCurso && data?.inscripciones?.total_personas_anio != null && (
            <p className="estadisticas-ayuda" style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: '#555' }}>
              Total del año: <strong>{data.inscripciones.total_personas_anio} personas</strong> (cada persona cuenta una vez aunque se anote en más de un curso).
            </p>
          )}
          {situacionPorCurso && (
            <p className="estadisticas-ayuda" style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: '#555' }}>
              Inscriptos solo en el curso elegido. El total del año ({data?.inscripciones?.total_personas_anio ?? '—'} personas) cuenta a cada persona una sola vez.
            </p>
          )}
          {situacionLaboral.length > 0 ? (() => {
            const totalSituacion = situacionLaboral.reduce((s, d) => s + (d.cantidad || 0), 0);
            return (
            <div className="estadisticas-chart-wrap estadisticas-pie-wrap">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={situacionLaboral}
                    dataKey="cantidad"
                    nameKey="nombre"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={58}
                    label={({ nombre, cantidad, midAngle, outerRadius, cx, cy }) => {
                      const RADIAN = Math.PI / 180;
                      const rOuter = outerRadius + 28;
                      const xOut = cx + rOuter * Math.cos(-midAngle * RADIAN);
                      const yOut = cy + rOuter * Math.sin(-midAngle * RADIAN);
                      const isRight = Math.cos(-midAngle * RADIAN) >= 0;
                      const pct = totalSituacion ? ((cantidad / totalSituacion) * 100).toFixed(1) : '0';
                      return (
                        <g>
                          <line x1={cx + outerRadius * Math.cos(-midAngle * RADIAN)} y1={cy + outerRadius * Math.sin(-midAngle * RADIAN)} x2={xOut} y2={yOut} stroke="#999" strokeWidth={1} />
                          <text x={xOut} y={yOut} fill="#333" textAnchor={isRight ? 'start' : 'end'} dominantBaseline="central" style={{ fontSize: 12, fontWeight: 600 }} dx={isRight ? 6 : -6}>
                            {nombre}
                          </text>
                          <text x={xOut} y={yOut} fill="#555" textAnchor={isRight ? 'start' : 'end'} dominantBaseline="central" style={{ fontSize: 12 }} dx={isRight ? 6 : -6} dy={14}>
                            {pct}%
                          </text>
                        </g>
                      );
                    }}
                    labelLine={false}
                  >
                    {situacionLaboral.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} (${totalSituacion ? ((value / totalSituacion) * 100).toFixed(1) : 0}%)`, 'Personas']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            );
          })() : (
            <p className="estadisticas-sin-datos">
              {situacionPorCurso && !situacionCursoId ? 'Seleccioná un curso para ver la situación laboral.' : 'No hay datos de situación laboral para el período seleccionado.'}
            </p>
          )}
        </section>

        {/* Nivel de estudio */}
        <section className="estadisticas-section">
          <h2>Nivel de estudio</h2>
          <div className="estadisticas-chart-opciones" style={{ marginBottom: '0.75rem' }}>
            <label className="estadisticas-checkbox-label">
              <input
                type="checkbox"
                checked={estudioPorCurso}
                onChange={(e) => {
                  setEstudioPorCurso(e.target.checked);
                  if (!e.target.checked) setEstudioCursoId('');
                }}
              />
              <span>Mostrar por curso</span>
            </label>
            {estudioPorCurso && cursosDelAnio.length > 0 && (
              <label className="estadisticas-select-curso-label">
                Curso
                <select
                  value={estudioCursoId}
                  onChange={(e) => setEstudioCursoId(e.target.value)}
                  className="estadisticas-select-curso"
                >
                  <option value="">Seleccionar curso...</option>
                  {cursosDelAnio.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {!estudioPorCurso && data?.inscripciones?.total_personas_anio != null && (
            <p className="estadisticas-ayuda" style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: '#555' }}>
              Total del año: <strong>{data.inscripciones.total_personas_anio} personas</strong> (cada persona cuenta una vez aunque se anote en más de un curso).
            </p>
          )}
          {estudioPorCurso && (
            <p className="estadisticas-ayuda" style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: '#555' }}>
              Inscriptos solo en el curso elegido. El total del año ({data?.inscripciones?.total_personas_anio ?? '—'} personas) cuenta a cada persona una sola vez.
            </p>
          )}
          {nivelEstudio.length > 0 ? (() => {
            const totalEstudio = nivelEstudio.reduce((s, d) => s + (d.cantidad || 0), 0);
            return (
            <div className="estadisticas-chart-wrap estadisticas-pie-wrap">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={nivelEstudio}
                    dataKey="cantidad"
                    nameKey="nombre"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={58}
                    label={({ nombre, cantidad, midAngle, outerRadius, cx, cy }) => {
                      const RADIAN = Math.PI / 180;
                      const rOuter = outerRadius + 28;
                      const xOut = cx + rOuter * Math.cos(-midAngle * RADIAN);
                      const yOut = cy + rOuter * Math.sin(-midAngle * RADIAN);
                      const isRight = Math.cos(-midAngle * RADIAN) >= 0;
                      const pct = totalEstudio ? ((cantidad / totalEstudio) * 100).toFixed(1) : '0';
                      return (
                        <g>
                          <line x1={cx + outerRadius * Math.cos(-midAngle * RADIAN)} y1={cy + outerRadius * Math.sin(-midAngle * RADIAN)} x2={xOut} y2={yOut} stroke="#999" strokeWidth={1} />
                          <text x={xOut} y={yOut} fill="#333" textAnchor={isRight ? 'start' : 'end'} dominantBaseline="central" style={{ fontSize: 12, fontWeight: 600 }} dx={isRight ? 6 : -6}>
                            {nombre}
                          </text>
                          <text x={xOut} y={yOut} fill="#555" textAnchor={isRight ? 'start' : 'end'} dominantBaseline="central" style={{ fontSize: 12 }} dx={isRight ? 6 : -6} dy={14}>
                            {pct}%
                          </text>
                        </g>
                      );
                    }}
                    labelLine={false}
                  >
                    {nivelEstudio.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} (${totalEstudio ? ((value / totalEstudio) * 100).toFixed(1) : 0}%)`, 'Personas']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            );
          })() : (
            <p className="estadisticas-sin-datos">
              {estudioPorCurso && !estudioCursoId ? 'Seleccioná un curso para ver el nivel de estudio.' : 'No hay datos de nivel de estudio para el período seleccionado.'}
            </p>
          )}
        </section>
      </div>
    </div>
  );
};

export default Estadisticas;
